import React, { useState, useEffect } from 'react';
import { FaRobot, FaTrash, FaCheck, FaTimes, FaSearch, FaFilter, FaFileAlt, FaQuestionCircle } from 'react-icons/fa';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy, where, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import './ModerationAdmin.css';

const ModerationAdmin = () => {
  const [moderationLogs, setModerationLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  const [stats, setStats] = useState({
    totalLogs: 0,
    approvedCount: 0,
    rejectedCount: 0,
    materialCount: 0,
    questionCount: 0
  });

  useEffect(() => {
    fetchModerationLogs();
  }, [filter, dateRange]);

  const fetchModerationLogs = async () => {
    try {
      setLoading(true);
      
      // Create base query
      let logsQuery = query(
        collection(db, 'moderationLogs'),
        orderBy('timestamp', 'desc')
      );
      
      // Apply content type filter if needed
      if (filter === 'materials') {
        logsQuery = query(
          collection(db, 'moderationLogs'),
          where('contentType', '==', 'material'),
          orderBy('timestamp', 'desc')
        );
      } else if (filter === 'questions') {
        logsQuery = query(
          collection(db, 'moderationLogs'),
          where('contentType', '==', 'question'),
          orderBy('timestamp', 'desc')
        );
      } else if (filter === 'approved') {
        logsQuery = query(
          collection(db, 'moderationLogs'),
          where('approved', '==', true),
          orderBy('timestamp', 'desc')
        );
      } else if (filter === 'rejected') {
        logsQuery = query(
          collection(db, 'moderationLogs'),
          where('approved', '==', false),
          orderBy('timestamp', 'desc')
        );
      }
      
      // Apply date filter if needed
      // Note: This would be more efficient with a compound query in a production environment
      
      const snapshot = await getDocs(logsQuery);
      let logsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp || 'Unknown'
      }));
      
      // Apply date filtering on client side if necessary
      if (dateRange.from) {
        const fromDate = new Date(dateRange.from).getTime();
        logsData = logsData.filter(log => {
          const logDate = new Date(log.timestamp).getTime();
          return logDate >= fromDate;
        });
      }
      
      if (dateRange.to) {
        const toDate = new Date(dateRange.to).getTime() + (24 * 60 * 60 * 1000); // Include the whole day
        logsData = logsData.filter(log => {
          const logDate = new Date(log.timestamp).getTime();
          return logDate <= toDate;
        });
      }
      
      setModerationLogs(logsData);
      calculateStats(logsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching moderation logs:', error);
      setLoading(false);
    }
  };

  const calculateStats = (logsData) => {
    const totalLogs = logsData.length;
    const approvedCount = logsData.filter(log => log.approved).length;
    const rejectedCount = logsData.filter(log => !log.approved).length;
    const materialCount = logsData.filter(log => log.contentType === 'material').length;
    const questionCount = logsData.filter(log => log.contentType === 'question').length;
    
    setStats({
      totalLogs,
      approvedCount,
      rejectedCount,
      materialCount,
      questionCount
    });
  };

  const handleDeleteLog = async (id) => {
    if (window.confirm('Are you sure you want to delete this moderation log?')) {
      try {
        await deleteDoc(doc(db, 'moderationLogs', id));
        setModerationLogs(moderationLogs.filter(log => log.id !== id));
        fetchModerationLogs(); // Refresh data and stats
      } catch (error) {
        console.error('Error deleting log:', error);
      }
    }
  };

  const handleOverrideDecision = async (log) => {
    const message = log.approved 
      ? 'Are you sure you want to reject this content that was previously approved?' 
      : 'Are you sure you want to approve this content that was previously rejected?';
      
    if (window.confirm(message)) {
      try {
        // Update the moderation log
        await updateDoc(doc(db, 'moderationLogs', log.id), {
          approved: !log.approved,
          reviewStatus: 'manual',
          reason: log.approved 
            ? 'Admin override: Rejected content that was previously approved by AI' 
            : 'Admin override: Approved content that was previously rejected by AI'
        });
        
        // If the content has already been saved to the database (i.e., not 'pre-upload' or 'pre-submission')
        if (log.contentId !== 'pre-upload' && log.contentId !== 'pre-submission') {
          // Update the content visibility in the appropriate collection
          const contentCollection = log.contentType === 'material' ? 'materials' : 'questions';
          await updateDoc(doc(db, contentCollection, log.contentId), {
            isVisible: !log.approved, // Opposite of current approval status
            moderationApproved: !log.approved
          });
        }
        
        fetchModerationLogs(); // Refresh data
      } catch (error) {
        console.error('Error overriding decision:', error);
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const filteredLogs = moderationLogs.filter(log => 
    log.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.contentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.userId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="moderation-admin-container">
      <h2 className="moderation-admin-title">
        <FaRobot /> AI Content Moderation Dashboard
      </h2>
      
      <div className="moderation-stats">
        <div className="stat-card">
          <h3>Total Logs</h3>
          <p>{stats.totalLogs}</p>
        </div>
        <div className="stat-card approved">
          <h3>Approved</h3>
          <p>{stats.approvedCount} ({Math.round((stats.approvedCount / stats.totalLogs || 0) * 100)}%)</p>
        </div>
        <div className="stat-card rejected">
          <h3>Rejected</h3>
          <p>{stats.rejectedCount} ({Math.round((stats.rejectedCount / stats.totalLogs || 0) * 100)}%)</p>
        </div>
        <div className="stat-card materials">
          <h3>Materials</h3>
          <p>{stats.materialCount}</p>
        </div>
        <div className="stat-card questions">
          <h3>Questions</h3>
          <p>{stats.questionCount}</p>
        </div>
      </div>
      
      <div className="log-controls">
        <div className="search-bar">
          <FaSearch />
          <input 
            type="text" 
            placeholder="Search logs..." 
            value={searchTerm} 
            onChange={handleSearch} 
          />
        </div>
        
        <div className="date-filter">
          <label>
            From:
            <input 
              type="date" 
              name="from" 
              value={dateRange.from}
              onChange={handleDateChange}
            />
          </label>
          <label>
            To:
            <input 
              type="date" 
              name="to" 
              value={dateRange.to}
              onChange={handleDateChange}
            />
          </label>
        </div>
        
        <div className="filter-buttons">
          <button 
            className={filter === 'all' ? 'active' : ''} 
            onClick={() => setFilter('all')}
          >
            All Logs
          </button>
          <button 
            className={filter === 'materials' ? 'active' : ''} 
            onClick={() => setFilter('materials')}
          >
            <FaFileAlt /> Materials
          </button>
          <button 
            className={filter === 'questions' ? 'active' : ''} 
            onClick={() => setFilter('questions')}
          >
            <FaQuestionCircle /> Questions
          </button>
          <button 
            className={filter === 'approved' ? 'active' : ''} 
            onClick={() => setFilter('approved')}
          >
            <FaCheck /> Approved
          </button>
          <button 
            className={filter === 'rejected' ? 'active' : ''} 
            onClick={() => setFilter('rejected')}
          >
            <FaTimes /> Rejected
          </button>
        </div>
      </div>
      
      {loading ? (
        <p className="loading-text">Loading moderation logs...</p>
      ) : (
        <div className="logs-list">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Content Type</th>
                <th>User ID</th>
                <th>Decision</th>
                <th>Reason</th>
                <th>Review Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length > 0 ? (
                filteredLogs.map(log => (
                  <tr key={log.id} className={log.approved ? 'approved-log' : 'rejected-log'}>
                    <td>{new Date(log.timestamp).toLocaleString()}</td>
                    <td className="content-type">
                      {log.contentType === 'material' ? <FaFileAlt /> : <FaQuestionCircle />}
                      {log.contentType}
                    </td>
                    <td>{log.userId}</td>
                    <td className={`decision ${log.approved ? 'approved' : 'rejected'}`}>
                      {log.approved ? <FaCheck /> : <FaTimes />}
                      {log.approved ? 'Approved' : 'Rejected'}
                    </td>
                    <td className="reason">{log.reason}</td>
                    <td className="review-status">
                      <span className={log.reviewStatus === 'auto' ? 'auto' : 'manual'}>
                        {log.reviewStatus === 'auto' ? 'AI' : 'Human'}
                      </span>
                    </td>
                    <td className="log-actions">
                      <button 
                        className="override-btn" 
                        onClick={() => handleOverrideDecision(log)}
                        title={log.approved ? "Mark as rejected" : "Mark as approved"}
                      >
                        {log.approved ? <FaTimes /> : <FaCheck />}
                      </button>
                      <button 
                        className="delete-btn" 
                        onClick={() => handleDeleteLog(log.id)}
                        title="Delete log"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="no-logs">
                    No moderation logs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ModerationAdmin; 